'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { TTermsForm, ZTermsForm } from '@/features/sign-up'
import { Button, Form, Separator, TextButton } from '@/shared/ui'

type Props = {
  defaultValues?: TTermsForm
  onFormFieldChange: (formValues: TTermsForm) => void
  onSubmit: (formValues: TTermsForm) => void
}

export const TermsForm = ({
  onSubmit,
  defaultValues,
  onFormFieldChange,
}: Props) => {
  const form = useForm<TTermsForm>({
    resolver: zodResolver(ZTermsForm),
    defaultValues,
  })

  const watch = useWatch({
    control: form.control,
  })

  const { termsOfService, privacyConsent, marketingAgree, ageOver19 } = watch

  const isRequiredAllChecked = termsOfService && privacyConsent && ageOver19
  const isAllChecked = isRequiredAllChecked && marketingAgree

  const handleSelectAll = (checked: boolean) => {
    form.setValue('termsOfService', checked)
    form.setValue('privacyConsent', checked)
    form.setValue('marketingAgree', checked)
    form.setValue('ageOver19', checked)
  }

  useEffect(() => {
    onFormFieldChange(watch as TTermsForm)
  }, [watch, onFormFieldChange])

  return (
    <Form.Root form={form} className="space-y-6">
      <Form.Field name="">
        <Form.Checkbox
          weight="bold"
          checked={isAllChecked}
          onCheckedChange={handleSelectAll}
        >
          전체동의
        </Form.Checkbox>
      </Form.Field>

      <Separator />

      <div className="flex items-center justify-between">
        <Form.Field name="termsOfService">
          <Form.Checkbox size="small" weight="medium">
            [필수] 서비스 이용약관 동의
          </Form.Checkbox>
        </Form.Field>
        <TextButton underline color="gray" asChild>
          <a href="/terms/using" target="_blank" rel="noopener noreferrer">
            보기
          </a>
        </TextButton>
      </div>
      <div className="flex items-center justify-between">
        <Form.Field name="privacyConsent">
          <Form.Checkbox size="small" weight="medium">
            [필수] 개인정보 수집 및 이용 동의
          </Form.Checkbox>
        </Form.Field>
        <TextButton underline color="gray" asChild>
          <a
            href="/terms/privacy-consent"
            target="_blank"
            rel="noopener noreferrer"
          >
            보기
          </a>
        </TextButton>
      </div>
      <div className="flex items-center justify-between">
        <Form.Field name="marketingAgree">
          <Form.Checkbox size="small" weight="medium">
            [선택] 마케팅 정보 수신 동의
          </Form.Checkbox>
        </Form.Field>
        <TextButton underline color="gray" asChild>
          <a href="/terms/marketing" target="_blank" rel="noopener noreferrer">
            보기
          </a>
        </TextButton>
      </div>
      <Form.Field name="ageOver19">
        <Form.Checkbox size="small" weight="medium">
          [필수] 만 19세 이상입니다.
        </Form.Checkbox>
      </Form.Field>

      <Button
        size="large"
        className="mt-9"
        type="submit"
        fullWidth
        onClick={form.handleSubmit(onSubmit)}
        disabled={!isRequiredAllChecked}
      >
        다음으로
      </Button>
    </Form.Root>
  )
}
